import React, { useState, useEffect } from 'react';
import { Save, X, Users, UserCheck } from 'lucide-react';
import axios from 'axios';

const UserPreferences = ({ onClose }) => {
  const [preferences, setPreferences] = useState({
    preferredPlayers: [],
    preferredStorytellers: [],
    notes: ''
  });
  const [allUsers, setAllUsers] = useState([]);
  const [storytellers, setStorytellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Maximum limits for preferred players and storytellers
  const MAX_PREFERRED_PLAYERS = 5;
  const MAX_PREFERRED_STORYTELLERS = 3;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [preferencesRes, usersRes, storytellersRes] = await Promise.all([
        axios.get('/api/users/preferences'),
        axios.get('/api/users/all'),
        axios.get('/api/users/storytellers/list')
      ]);
      
      setPreferences(preferencesRes.data);
      setAllUsers(usersRes.data);
      setStorytellers(storytellersRes.data);
    } catch (error) {
      console.error('Failed to fetch preferences data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put('/api/users/preferences', preferences);
      onClose();
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const addToArray = (field, userId) => {
    const currentArray = preferences[field];
    const maxLimit = field === 'preferredPlayers' ? MAX_PREFERRED_PLAYERS : MAX_PREFERRED_STORYTELLERS;
    
    if (!currentArray.includes(userId) && currentArray.length < maxLimit) {
      setPreferences(prev => ({
        ...prev,
        [field]: [...prev[field], userId]
      }));
    }
  };

  const removeFromArray = (field, userId) => {
    setPreferences(prev => ({
      ...prev,
      [field]: prev[field].filter(id => id !== userId)
    }));
  };

  const getUserById = (userId) => {
    // Check in storytellers first, then in all users
    const storyteller = storytellers.find(user => user.id === userId);
    if (storyteller) return storyteller;
    return allUsers.find(user => user.id === userId);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4">
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-primary">Player Preferences</h2>
          <button
            onClick={onClose}
            className="text-secondary hover:text-primary"
          >
            <X size={24} />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Preferred Players */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-primary flex items-center space-x-2">
              <UserCheck size={20} />
              <span>Preferred Players</span>
              <span className="text-sm text-secondary">
                ({preferences.preferredPlayers.length}/{MAX_PREFERRED_PLAYERS})
              </span>
            </h3>
            
            <div className="space-y-2">
              {preferences.preferredPlayers.map(userId => {
                const user = getUserById(userId);
                return user ? (
                  <div key={userId} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      {user.avatar && user.discord_id && (
                        <img
                          src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                          alt={user.username}
                          className="w-6 h-6 rounded-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <span className="text-sm font-medium">{user.username}</span>
                    </div>
                    <button
                      onClick={() => removeFromArray('preferredPlayers', userId)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : null;
              })}
            </div>

            <div className="border rounded-lg p-3">
              <h4 className="text-sm font-medium text-secondary mb-2">Add Preferred Player</h4>
              {preferences.preferredPlayers.length >= MAX_PREFERRED_PLAYERS && (
                <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-800 dark:text-yellow-200">
                  Maximum of {MAX_PREFERRED_PLAYERS} preferred players reached
                </div>
              )}
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {allUsers
                  .filter(user => !preferences.preferredPlayers.includes(user.id))
                  .map(user => (
                    <button
                      key={user.id}
                      onClick={() => addToArray('preferredPlayers', user.id)}
                      disabled={preferences.preferredPlayers.length >= MAX_PREFERRED_PLAYERS}
                      className="flex items-center space-x-2 w-full p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      {user.avatar && user.discord_id && (
                        <img
                          src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                          alt={user.username}
                          className="w-5 h-5 rounded-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <span className="text-sm">{user.username}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* Preferred Storytellers */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-primary flex items-center space-x-2">
              <Users size={20} />
              <span>Preferred Storytellers</span>
              <span className="text-sm text-secondary">
                ({preferences.preferredStorytellers.length}/{MAX_PREFERRED_STORYTELLERS})
              </span>
            </h3>
            
            <div className="space-y-2">
              {preferences.preferredStorytellers.map(userId => {
                const user = getUserById(userId);
                return user ? (
                  <div key={userId} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center space-x-2">
                      {user.avatar && user.discord_id && (
                        <img
                          src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                          alt={user.username}
                          className="w-6 h-6 rounded-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <span className="text-sm font-medium">{user.username}</span>
                    </div>
                    <button
                      onClick={() => removeFromArray('preferredStorytellers', userId)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ) : null;
              })}
            </div>

            <div className="border rounded-lg p-3">
              <h4 className="text-sm font-medium text-secondary mb-2">Add Preferred Storyteller</h4>
              {preferences.preferredStorytellers.length >= MAX_PREFERRED_STORYTELLERS && (
                <div className="mb-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded text-sm text-yellow-800 dark:text-yellow-200">
                  Maximum of {MAX_PREFERRED_STORYTELLERS} preferred storytellers reached
                </div>
              )}
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {storytellers
                  .filter(user => !preferences.preferredStorytellers.includes(user.id))
                  .map(user => (
                    <button
                      key={user.id}
                      onClick={() => addToArray('preferredStorytellers', user.id)}
                      disabled={preferences.preferredStorytellers.length >= MAX_PREFERRED_STORYTELLERS}
                      className="flex items-center space-x-2 w-full p-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                    >
                      {user.avatar && user.discord_id && (
                        <img
                          src={`https://cdn.discordapp.com/avatars/${user.discord_id}/${user.avatar}.png`}
                          alt={user.username}
                          className="w-5 h-5 rounded-full"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <span className="text-sm">{user.username}</span>
                    </button>
                  ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-primary">Additional Notes</h3>
            <textarea
              value={preferences.notes}
              onChange={(e) => setPreferences(prev => ({ ...prev, notes: e.target.value }))}
              className="input w-full h-32 resize-none"
              placeholder="Any additional preferences or notes..."
            />
          </div>
        </div>

        <div className="flex space-x-3 mt-6">
          <button
            onClick={onClose}
            className="btn btn-secondary flex-1"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary flex-1 flex items-center justify-center space-x-2"
          >
            <Save size={16} />
            <span>{saving ? 'Saving...' : 'Save Preferences'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserPreferences; 